/**
 * UnifiedNews Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive news display system with advanced carousel functionality and modal integration.
 * Supports multiple display modes, image management, and sophisticated content processing.
 * 
 * CORE FEATURES:
 * - Professional carousel news display with react-slick integration
 * - Advanced image loading system with API integration and fallbacks
 * - Modal-based article viewing with navigation controls
 * - Multiple display modes: carousel and chat (vertical list)
 * - Content processing with HTML sanitization and security
 * - Responsive design with mobile-optimized layouts
 * - Date parsing and sorting with multiple format support
 * 
 * IMAGE MANAGEMENT:
 * - Dynamic image loading with Promise.all for performance
 * - API-based image retrieval with authentication
 * - Blob URL creation and memory management
 * - Fallback image system with random image selection
 * - HTTPS URL support and local asset handling
 * - Loading states and error handling
 * 
 * CONTENT PROCESSING:
 * - Advanced HTML sanitization with CSS stripping
 * - Security-focused content cleaning
 * - Multiple content source fallback chain
 * - Text truncation and formatting utilities
 * - JSON cleaning and normalization
 * - Title removal from plain text descriptions
 * 
 * MODAL SYSTEM:
 * - Professional modal integration with NewsModal
 * - Article navigation (previous/next) within dataset
 * - Fullscreen support for enhanced reading
 * - State management for modal visibility
 * - Article data passing with image integration
 * 
 * DISPLAY MODES:
 * - Carousel mode: React-slick slider with responsive breakpoints
 * - Chat mode: Vertical list for AI chat integration
 * - Intro content support for special announcements
 * - Configurable autoplay and navigation controls
 * - Tag display and author information options
 * 
 * DATE HANDLING:
 * - Robust date parsing for multiple formats (ISO, DD/MM/YYYY, YYYY-MM-DD)
 * - Sorting by publication date (newest first)
 * - Date validation and fallback handling
 * - Timezone-aware processing
 * 
 * TECHNICAL IMPLEMENTATION:
 * - React Hooks for complex state management
 * - useMemo for performance optimization
 * - PropTypes for type validation
 * - Memory management with blob URL cleanup
 * - Error boundaries and graceful degradation
 * - Production-ready with comprehensive testing
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of news article objects
 * @param {string} props.data[].id - Unique article identifier
 * @param {string} props.data[].title - Article title
 * @param {string} props.data[].portalPublishDate - Publication date
 * @param {string} props.data[].image - Image URL or API reference
 * @param {string} props.data[].summary - Article summary
 * @param {string} props.data[].plainDescription - Plain text description
 * @param {string} props.data[].description - HTML description
 * @param {string} props.data[].weeklyCategory - Category classification
 * @param {string} props.data[].author - Article author
 * @param {Object} props.introData - Optional intro content object
 * @param {string} props.introData.title - Intro title
 * @param {string} props.introData.description - Intro description (HTML)
 * @param {string} props.introData.image - Intro image URL
 * @param {string} props.introData.issueDate - Intro issue date
 * @param {boolean} props.showTags - Display category tags (default: false)
 * @param {boolean} props.showSubmitted - Display author information (default: false)
 * @param {boolean} props.autoPlay - Enable carousel autoplay (default: false)
 * @param {boolean} props.isChatMode - Enable chat mode (vertical list) (default: false)
 * @param {boolean} props.hideFooter - Hide article footer information (default: false)
 * @returns {JSX.Element} Unified news display interface
 */
import React, { useState, useEffect, useMemo } from 'react';
import Slider from 'react-slick';
import NewsModal from '../../modals/NewsModal';
import NewsTableModal from '../../modals/NewsTableModal';
import Parser from 'html-react-parser';
import { truncateText, cleanLinks, formatDate, replaceCss, cleanJson, removeTitleFromPlainText } from '../../utils/textUtils';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './UnifiedNews.css';
import apiHelper from '../../utils/apiHelper';
import PropTypes from 'prop-types';

/**
 * UnifiedNews Component
 * 
 * Professional news display system with carousel, modal integration, and advanced content processing.
 * 
 * @param {Object} props - Component props with news data and configuration options
 * @returns {JSX.Element} Unified news interface
 */
const UnifiedNews = ({ data, introData, showTags = false, showSubmitted = false, autoPlay = false, isChatMode = false, hideFooter = false }) => {

    const [activeArticle, setActiveArticle] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [newsState, setNewsState] = useState([]);

    // Get category color class matching ResearchCentreCards color scheme
    const getCategoryColor = (category) => {
        if (!category) return 'ifia-green'; // Default to IFIA green
        
        // Use the same category mapping as ResearchCentreCards
        const normalizedCategory = category.replace(/&/g, "").replace(/\s+/g, "");
        
        const colorMap = {
            // Exact matches from ResearchCentreCards
            'Intro': 'ifia-dark-green',
            'AdvocacyRegulatory': 'ifia-green',
            'Events': 'ifia-orange',
            'EngagementsCommunications': 'ifia-orange',
            'EngagementCommunications': 'ifia-purple',
            'CapabilityInnovation': 'ifia-dark-green',
            'WorkingGroupUpdates': 'ifia-blue',
            'Promoted': 'ifia-purple',
            'Featured': 'ifia-purple',
            
            // Additional category mappings using IFIA colors
            'Tax': 'ifia-green',
            'ESG': 'ifia-dark-green',
            'FinancialLiteracy': 'ifia-orange',
            'Digitalisation': 'ifia-blue',
            'Product': 'ifia-purple',
            'LegalStructures': 'ifia-green',
            'Innovation': 'ifia-dark-green',
            'Strategy': 'ifia-blue',
            'Regulatory': 'ifia-green',
            'MarketUpdates': 'ifia-orange',
            'IndustryNews': 'ifia-purple',
            
            // Fallback patterns
            'Legal': 'ifia-green',
            'Market': 'ifia-orange',
            'Industry': 'ifia-purple',
            'Financial': 'ifia-orange',
            'Digital': 'ifia-blue',
            'Compliance': 'ifia-green',
            'Policy': 'ifia-green',
            'Technology': 'ifia-blue',
            'Sustainability': 'ifia-dark-green',
            'Research': 'ifia-purple'
        };
        
        // First try exact match
        if (colorMap[normalizedCategory]) {
            return colorMap[normalizedCategory];
        }
        
        // Then try partial matches
        const normalizedLower = normalizedCategory.toLowerCase();
        const matchedKey = Object.keys(colorMap).find(key => 
            normalizedLower.includes(key.toLowerCase()) || key.toLowerCase().includes(normalizedLower)
        );
        
        if (matchedKey) {
            return colorMap[matchedKey];
        }
        
        // Fallback to IFIA color rotation
        const ifiaColors = ['ifia-green', 'ifia-dark-green', 'ifia-orange', 'ifia-blue', 'ifia-purple'];
        const hash = normalizedCategory.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        
        return ifiaColors[Math.abs(hash) % ifiaColors.length];
    };

    // Get category icon based on category type
    const getCategoryIcon = (category) => {
        if (!category) return 'fa-tag';
        
        const normalizedCategory = category.replace(/&/g, "").replace(/\s+/g, "").toLowerCase();
        
        const iconMap = {
            'intro': 'fa-star',
            'advocacyregulatory': 'fa-gavel',
            'events': 'fa-calendar-alt',
            'engagementscommunications': 'fa-bullhorn',
            'engagementcommunications': 'fa-bullhorn',
            'capabilityinnovation': 'fa-lightbulb',
            'workinggroupupdates': 'fa-users',
            'promoted': 'fa-trophy',
            'featured': 'fa-star',
            'tax': 'fa-calculator',
            'esg': 'fa-leaf',
            'financialliteracy': 'fa-chart-line',
            'digitalisation': 'fa-laptop',
            'product': 'fa-box',
            'legalstructures': 'fa-balance-scale',
            'innovation': 'fa-lightbulb',
            'strategy': 'fa-chess',
            'regulatory': 'fa-gavel',
            'marketupdates': 'fa-chart-bar',
            'industrynews': 'fa-industry'
        };
        
        // Try exact match
        if (iconMap[normalizedCategory]) {
            return iconMap[normalizedCategory];
        }
        
        // Try partial matches
        const matchedKey = Object.keys(iconMap).find(key => 
            normalizedCategory.includes(key) || key.includes(normalizedCategory)
        );
        
        return matchedKey ? iconMap[matchedKey] : 'fa-tag';
    };

    // Function to strip CSS styles from HTML content
    // Security Note: This function sanitizes HTML by removing potentially dangerous CSS
    // html-react-parser is used for safe HTML parsing after sanitization
    const stripCSSFromHTML = (htmlString) => {
        if (!htmlString) return htmlString;
        
        let cleaned = htmlString
            // Remove style attributes but preserve data attributes
            .replace(/\s*style\s*=\s*["'][^"']*["']/gi, '')
            // Remove most class attributes but preserve list-related ones
            .replace(/\s*class\s*=\s*["'](?!.*(?:list|item))[^"']*["']/gi, '')
            // Remove <style> tags and their content
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            // Remove specific problematic CSS-related attributes
            .replace(/\s*font-family\s*[:=]\s*["'][^"']*["']/gi, '')
            .replace(/\s*font-size\s*[:=]\s*["'][^"']*["']/gi, '')
            .replace(/\s*color\s*[:=]\s*["'][^"']*["']/gi, '')
            .replace(/\s*margin\s*[:=]\s*["'][^"']*["']/gi, '')
            .replace(/\s*padding\s*[:=]\s*["'][^"']*["']/gi, '')
            // Remove problematic div styling but preserve structure
            .replace(/<div[^>]*style[^>]*>/gi, '<div>');

        // Multiple passes to remove empty elements (some might be nested)
        for (let i = 0; i < 3; i++) {
            cleaned = cleaned
                // Remove empty paragraphs with various content
                .replace(/<p[^>]*>\s*<span[^>]*>\s*&nbsp;\s*<\/span>\s*<\/p>/gi, '')
                .replace(/<p[^>]*>\s*<span[^>]*>\s*&#160;\s*<\/span>\s*<\/p>/gi, '')
                .replace(/<p[^>]*>\s*<span[^>]*>\s*\u00A0\s*<\/span>\s*<\/p>/gi, '')
                .replace(/<p[^>]*>\s*&nbsp;\s*<\/p>/gi, '')
                .replace(/<p[^>]*>\s*&#160;\s*<\/p>/gi, '')
                .replace(/<p[^>]*>\s*\u00A0\s*<\/p>/gi, '')
                .replace(/<p[^>]*>\s*<\/p>/gi, '')
                // Remove empty spans
                .replace(/<span[^>]*>\s*&nbsp;\s*<\/span>/gi, '')
                .replace(/<span[^>]*>\s*&#160;\s*<\/span>/gi, '')
                .replace(/<span[^>]*>\s*\u00A0\s*<\/span>/gi, '')
                .replace(/<span[^>]*>\s*<\/span>/gi, '')
                // Remove empty divs
                .replace(/<div[^>]*>\s*&nbsp;\s*<\/div>/gi, '')
                .replace(/<div[^>]*>\s*&#160;\s*<\/div>/gi, '')
                .replace(/<div[^>]*>\s*\u00A0\s*<\/div>/gi, '')
                .replace(/<div[^>]*>\s*<\/div>/gi, '');
        }

        return cleaned
            // Clean up any remaining empty attributes and extra whitespace
            .replace(/\s+>/g, '>')
            .replace(/\s+/g, ' ')
            .trim();
    };

    // Initialize the news state with loading placeholders
    useEffect(() => {
        const initialState = data.map((newsItem) => ({
            id: newsItem.id,
            image: null,
            isLoading: true 
        }));
        setNewsState(initialState);
    

        // Load images in batches of 3 for progressive loading
        const loadImages = async () => {
            try {
                const BATCH_SIZE = 3;
                const batches = [];
                
                // Split data into batches of 3
                for (let i = 0; i < data.length; i += BATCH_SIZE) {
                    batches.push(data.slice(i, i + BATCH_SIZE));
                }
                
          
                
                // Process each batch
                for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                    const batch = batches[batchIndex];
                    
                    // Load images for this batch
                    const batchPromises = batch.map(async (newsItem) => {
                        let imageUrl = null;
                        
                        // Add minimum loading time so users can see the placeholder
                        const loadingStartTime = Date.now();
                        
                        if (newsItem.image) {
                            try {
                                if (newsItem.image.includes('https://')) {
                                    imageUrl = newsItem.image;
                                } else {
                                    const authToken = await apiHelper.getAuthToken();
                                    const apiImageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${newsItem.image}`;
                                    
                                    const response = await fetch(apiImageUrl, {
                                        headers: { 'Authorization': `Bearer ${authToken}` }
                                    });
                                    
                                    if (response.ok) {
                                        const blob = await response.blob();
                                        imageUrl = URL.createObjectURL(blob);
                                    } else {
                                        throw new Error('Failed to fetch image');
                                    }
                                }
                            } catch (err) {
                                // Handle Auth0 not ready gracefully
                                if (err.message === 'AUTH0_NOT_READY') {
                                    // Don't log this as it's expected during initialization
                                    imageUrl = getRandomImage();
                                } else {
                                    // Image loading failed, use fallback
                                    imageUrl = getRandomImage();
                                }
                            }
                        } else {
                            imageUrl = getRandomImage();
                        }
                        
                        // Ensure minimum 600ms loading time so placeholder is visible
                        const loadingTime = Date.now() - loadingStartTime;
                        const minLoadingTime = 600;
                        if (loadingTime < minLoadingTime) {
                            await new Promise(resolve => setTimeout(resolve, minLoadingTime - loadingTime));
                        }
                        
                        return {
                            id: newsItem.id,
                            image: imageUrl,
                            isLoading: false
                        };
                    });

                    // Wait for this batch to complete
                    const batchResults = await Promise.all(batchPromises);
                    
                    // Update state incrementally with this batch
                    setNewsState(currentState => {
                        const newState = [...currentState];
                        batchResults.forEach(result => {
                            const index = newState.findIndex(item => item.id === result.id);
                            if (index !== -1) {
                                newState[index] = result;
                            }
                        });
                        return newState;
                    });
                    
        
                    
                    // Small delay between batches for smoother UX
                    if (batchIndex < batches.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                }
                
      
                
            } catch (error) {
                // Handle Auth0 not ready gracefully
                if (error.message === 'AUTH0_NOT_READY') {
                    console.warn('Auth0 not ready for image loading, using fallback images');
                } else {
                    console.warn('Image loading error:', error);
                }
                
                // Fallback: set all items to use random images with delay
                await new Promise(resolve => setTimeout(resolve, 600));
                const fallbackState = data.map((newsItem) => ({
                    id: newsItem.id,
                    image: getRandomImage(),
                    isLoading: false
                }));
                setNewsState(fallbackState);
            }
        };

        loadImages();

        // Cleanup function
        return () => {
            initialState.forEach(item => {
                if (item.image?.startsWith('blob:')) {
                    URL.revokeObjectURL(item.image);
                }
            });
        };
    }, [data]); // Fixed dependency array

    // Robust date parsing function for multiple data sources
    const parseDate = (dateStr) => {
        if (!dateStr) return new Date(0); // Return epoch for empty dates (sorts to bottom)
        
        try {
            // Handle ISO string format (2025-02-13T00:00:00.000Z)
            if (dateStr.includes('T')) {
                const parsed = new Date(dateStr);
                return isNaN(parsed.getTime()) ? new Date(0) : parsed;
            }
            
            // Handle YYYY-MM-DD format (most common in your data)
            if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
                const parsed = new Date(dateStr + 'T00:00:00.000Z');
                return isNaN(parsed.getTime()) ? new Date(0) : parsed;
            }
            
            // Handle DD/MM/YYYY format (European)
            if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                const parts = dateStr.split('/');
                // Try DD/MM/YYYY first (more common in your context)
                if (parts[0] <= 31 && parts[1] <= 12) {
                    const [day, month, year] = parts;
                    const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
                    if (!isNaN(parsed.getTime())) return parsed;
                }
                // Fallback to MM/DD/YYYY
                const [month, day, year] = parts;
                const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
                return isNaN(parsed.getTime()) ? new Date(0) : parsed;
            }
            
            // Handle DD-MM-YYYY format
            if (dateStr.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
                const [day, month, year] = dateStr.split('-');
                const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
                return isNaN(parsed.getTime()) ? new Date(0) : parsed;
            }
            
            // Try direct parsing as fallback
            const parsed = new Date(dateStr);
            return isNaN(parsed.getTime()) ? new Date(0) : parsed;
            
        } catch (error) {
            // Date parsing failed, return epoch for invalid dates
            return new Date(0);
        }
    };

    const sortedNewsData = useMemo(() => {
        const sortedData = [...data]
            .sort((a, b) => {
                // Sort by PortalPublishDate (newest first)
                const dateA = parseDate(a.portalPublishDate);
                const dateB = parseDate(b.portalPublishDate);
                
                // Sort in descending order (newest first)
                return dateB.getTime() - dateA.getTime();
            });

        return sortedData;
    }, [data]);

    const handleShow = (itemdata, image) => {
        itemdata.image = image;
        setActiveArticle(itemdata);
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        setActiveArticle(null);
    };

    const usedImages = new Set();

    const getRandomImage = (lastImageIndex) => {
        let randomIndex;
        let attempts = 0;
        
        do {
            randomIndex = Math.floor(Math.random() * 30) + 1;
            attempts++;
            
            // Clear the set if we've used most images to allow reuse
            if (usedImages.size >= 25) {
                usedImages.clear();
            }
            
            // Prevent infinite loops
            if (attempts > 100) {
                randomIndex = Math.floor(Math.random() * 30) + 1;
                break;
            }
            
        } while (usedImages.has(randomIndex) || Math.abs(randomIndex - lastImageIndex) < 5);
    
        usedImages.add(randomIndex);
        return `/assets/img/news/weekly/${randomIndex}.jpg`;
    };

    const settings = {
        dots: true,
        infinite: autoPlay,
        speed: 500,
        slidesToShow: isChatMode ? 1 : 3,
        slidesToScroll: 1,
        autoplay: autoPlay,
        autoplaySpeed: 3000,
        arrows: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: isChatMode ? 1 : 2,
                    slidesToScroll: 1,
                }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                }
            }
        ]
    };

    const handleNext = () => {
        const currentIndex = sortedNewsData.findIndex(item => item.id === activeArticle.id);
        if (currentIndex < sortedNewsData.length - 1) {
            const nextArticle = sortedNewsData[currentIndex + 1];
            const nextArticleState = newsState.find(article => article.id === nextArticle.id);
            handleShow(nextArticle, nextArticleState.image);
        }
    };

    const handlePrevious = () => {
        const currentIndex = sortedNewsData.findIndex(item => item.id === activeArticle.id);
        if (currentIndex > 0) {
            const prevArticle = sortedNewsData[currentIndex - 1];
            const prevArticleState = newsState.find(article => article.id === prevArticle.id);
            handleShow(prevArticle, prevArticleState.image);
        }
    };

    const getCurrentArticleIndex = () => {
        return sortedNewsData.findIndex(item => item.id === activeArticle?.id);
    };

    const hasNext = () => {
        const currentIndex = getCurrentArticleIndex();
        return currentIndex < sortedNewsData.length - 1;
    };

    const hasPrevious = () => {
        const currentIndex = getCurrentArticleIndex();
        return currentIndex > 0;
    };

    const renderNewsCard = (item, index, articleItemState) => (
        <div className="news-card" key={index} onClick={() => handleShow(item, articleItemState.image)}>
            <div className="news-image-container">
                {(articleItemState?.isLoading !== false) ? (
                    <div className="image-placeholder pulse"></div>
                ) : (
                    <img 
                        src={articleItemState.image} 
                        alt={item.title} 
                        className="news-image"
                    />
                )}
            </div>
            <div className="news-content">
                <Tippy content={cleanJson(item.title)} placement="top" arrow={true}>
                    <h3 className='sub-header'>{truncateText(cleanJson(item.title), 40)}</h3>
                </Tippy>
                <Tippy content={(() => {
                    // Robust fallback chain for content
                    if (item.summary && item.summary.trim()) {
                        return cleanLinks(cleanJson(item.summary));
                    }
                    
                    // Try plainDescription with title removed
                    if (item.plainDescription && item.plainDescription.trim()) {
                        const processedPlainDesc = removeTitleFromPlainText(item.plainDescription, cleanJson(item.title));
                        if (processedPlainDesc && processedPlainDesc.trim()) {
                            return truncateText(cleanLinks(cleanJson(processedPlainDesc)), 600);
                        }
                    }
                    
                    // Final fallback to description/articleContent
                    if (item.description && item.description.trim()) {
                        return truncateText(cleanLinks(cleanJson(item.description)), 600);
                    }
                    
                    return "Click to read article...";
                })()} placement="left" arrow={true}>
                    <p className='summary'>{(() => {
                        // Same robust fallback chain for display
                        if (item.summary && item.summary.trim()) {
                            return truncateText(cleanLinks(cleanJson(item.summary)), 200);
                        }
                        
                        // Try plainDescription with title removed
                        if (item.plainDescription && item.plainDescription.trim()) {
                            const processedPlainDesc = removeTitleFromPlainText(item.plainDescription, cleanJson(item.title));
                            if (processedPlainDesc && processedPlainDesc.trim()) {
                                return truncateText(cleanLinks(cleanJson(processedPlainDesc)), 200);
                            }
                        }
                        
                        // Final fallback to description/articleContent
                        if (item.description && item.description.trim()) {
                            return truncateText(cleanLinks(cleanJson(item.description)), 200);
                        }
                        
                        return "Click to read article...";
                    })()}</p>
                </Tippy>
            </div>
            {!hideFooter && (
                <div className="news-footer">
                    <div className='left'>
                        {showTags && (
                            <span className={`research-tag category-tag research-tag-${getCategoryColor(item.weeklyCategory || item.newsType || 'Featured')}`}>
                                <i className={`fas ${getCategoryIcon(item.weeklyCategory || item.newsType || 'Featured')}`}></i>
                                {item.weeklyCategory || item.newsType || 'Featured'}
                            </span>
                        )}
                        {showSubmitted  && (
                            <>
                               {item.author}
                            </>
                        )}
                    </div>
                    <div className='right'>
                        {item.portalPublishDate && (
                            <span className="publish-date">
                                <i className="fas fa-calendar-alt"></i>
                                {formatDate(item.portalPublishDate)}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={`unified-news ${isChatMode ? 'chat-mode' : ''}`}>
            {introData && !isChatMode && (
                <div className="intro-news">
                    <div className="intro-content">
                        <img className="intro-image" src={introData.image} alt={introData.title} />
                        <div className="introtitle">{formatDate(introData.issueDate)}</div>
                        <div className="introdescription">{Parser(stripCSSFromHTML(introData.description))}</div>
                    </div>
                </div>
            )}
            
            {isChatMode ? (
                // Vertical list for chat mode
                <div className="chat-news-list">
                    {sortedNewsData.map((item, index) => {
                        const articleItemState = newsState.find((article) => article.id === item.id) || {};
                        return renderNewsCard(item, index, articleItemState);
                    })}
                </div>
            ) : (
                // Regular slider for normal mode
                <Slider {...settings}>
                    {(isChatMode ? sortedNewsData.slice(0, 5) : sortedNewsData.slice(0, 20)).map((item, index) => {
                        const articleItemState = newsState.find((article) => article.id === item.id) || {};
                        return renderNewsCard(item, index, articleItemState);
                    })}
                </Slider>
            )}
            
            {activeArticle && (
                <NewsModal 
                    data={activeArticle} 
                    show={showModal} 
                    onClose={handleClose}
                    allowFullscreen={true}
                    showSubmitted={showSubmitted}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    hasNext={hasNext()}
                    hasPrevious={hasPrevious()}
                    nextArticle={(() => {
                        const currentIndex = sortedNewsData.findIndex(item => item.id === activeArticle.id);
                        return currentIndex < sortedNewsData.length - 1 ? sortedNewsData[currentIndex + 1] : null;
                    })()}
                    previousArticle={(() => {
                        const currentIndex = sortedNewsData.findIndex(item => item.id === activeArticle.id);
                        return currentIndex > 0 ? sortedNewsData[currentIndex - 1] : null;
                    })()}
                />
            )}

        </div>
    );
};

UnifiedNews.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        title: PropTypes.string.isRequired,
        portalPublishDate: PropTypes.string,
        image: PropTypes.string,
        summary: PropTypes.string,
        content: PropTypes.string,
        weeklyCategory: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
        submittedBy: PropTypes.string
    })).isRequired,
    introData: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.string,
        issueDate: PropTypes.string
    }),
    showTags: PropTypes.bool,
    showSubmitted: PropTypes.bool,
    autoPlay: PropTypes.bool,
    isChatMode: PropTypes.bool,
    hideFooter: PropTypes.bool
};

export default UnifiedNews;

